/**
 * SettingsModal - Universal settings access via Cmd+,
 *
 * Theme-adaptive modal for accessing all 4 semantic layers:
 * - Layer 1: LayoutTheme (HOW to display)
 * - Layer 2: DesignTokenSet (VISUAL styling)
 * - Layer 3: ViewerProfile (WHAT to show)
 * - Layer 4: RolePersona (WHO evaluates)
 *
 * Adapts styling based on current LayoutTheme (e.g., Matrix Terminal = green).
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { useDesignToken } from '@/contexts/DesignTokenContext'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import { useRolePersona } from '@/contexts/RolePersonaContext'
import { useSession } from '@/lib/auth-client'
import type { LayoutTheme } from '@/types/layout-theme'
import type { DesignTokenSet } from '@/types/design-token-set'
import type { ViewerProfile } from '@/types/viewer-profile'
import type { RolePersona } from '@/types/role-persona'

interface SettingsModalProps {
  isOpen?: boolean
  onClose?: () => void
}

export function SettingsModal({ isOpen: controlledOpen, onClose }: SettingsModalProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'layout' | 'tokens' | 'view' | 'role'>('layout')

  const { currentTheme, setTheme: setLayoutTheme, availableThemes } = useLayoutTheme()
  const { currentTokenSet, setTokenSet, availableTokenSets } = useDesignToken()
  const { currentProfile, setProfile, availableProfiles } = useViewerProfile()
  const { currentPersona, setPersona, availablePersonas } = useRolePersona()
  const { data: session } = useSession()

  // Support both controlled and uncontrolled modes
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      setInternalOpen(false)
    }
  }, [onClose])

  // Check if user is superadmin
  const isSuperAdmin = session?.user?.email === 'musia@habitusnet.ch'

  // Filter out terminal-explorer theme for non-superadmins
  const visibleThemes = useMemo(() => {
    return availableThemes.filter(theme => {
      if (theme === 'terminal-explorer' && !isSuperAdmin) {
        return false
      }
      return true
    })
  }, [availableThemes, isSuperAdmin])

  // Keyboard shortcuts
  // Note: When controlled by parent (controlledOpen !== undefined),
  // parent is responsible for Cmd+, handling. We only handle Escape here.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Cmd+, when uncontrolled
      if (controlledOpen === undefined && (e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault()
        setInternalOpen(prev => !prev)
        return
      }

      // Close on Escape (always)
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleClose, controlledOpen])

  // Theme-adaptive styling
  const isTerminalTheme = currentTheme === 'matrix-terminal' || currentTheme === 'terminal-explorer'
  const isMacTheme = currentTheme === 'mac-os-classic' || currentTheme === 'mac-studio'

  // Color swatches for design token sets
  const tokenColors: Record<DesignTokenSet, { primary: string; accent: string }> = {
    elegant: { primary: '#1a1a1a', accent: '#d4af37' },
    'japanese-zen': { primary: '#2d3436', accent: '#6c7a89' },
    'scandinavian-hygge': { primary: '#34495e', accent: '#e67e22' },
    'mediterranean-sun': { primary: '#0984e3', accent: '#d63031' },
  }

  // Labels for options
  const layoutLabels: Record<LayoutTheme, string> = {
    'modern-cards': 'Modern Cards',
    'matrix-terminal': 'Matrix Terminal',
    'mac-os-classic': 'Mac OS Classic',
    'mac-studio': 'Mac Studio',
    'executive-dashboard': 'Executive Dashboard',
    'terminal-explorer': 'Terminal Explorer',
  }

  const tokenLabels: Record<DesignTokenSet, string> = {
    elegant: 'Elegant',
    'japanese-zen': 'Japanese Zen',
    'scandinavian-hygge': 'Scandinavian Hygge',
    'mediterranean-sun': 'Mediterranean Sun',
  }

  const viewLabels: Record<ViewerProfile, string> = {
    'public-view': 'Public View',
    'recruiter-view': 'Recruiter View',
    'technical-view': 'Technical View',
    'executive-view': 'Executive View',
  }

  const roleLabels: Record<RolePersona, string> = {
    candidate: 'Candidate',
    'hiring-manager': 'Hiring Manager',
    evaluator: 'Technical Evaluator',
    peer: 'Peer Reviewer',
  }

  // Theme-specific colors
  const getThemeColors = () => {
    if (isTerminalTheme) {
      return {
        bg: '#0a0a0a',
        surface: '#111',
        border: '#0f0',
        borderMuted: 'rgba(0, 255, 0, 0.3)',
        text: '#0f0',
        textMuted: 'rgba(0, 255, 0, 0.6)',
        accent: '#0f0',
        selected: 'rgba(0, 255, 0, 0.1)',
      }
    }
    if (isMacTheme) {
      return {
        bg: '#ddd',
        surface: '#eee',
        border: '#000',
        borderMuted: '#999',
        text: '#000',
        textMuted: '#666',
        accent: '#000',
        selected: '#bbb',
      }
    }
    // Default / modern
    return {
      bg: 'var(--color-background, #fff)',
      surface: 'var(--color-surface, #f8f8f8)',
      border: 'var(--color-border, #ddd)',
      borderMuted: 'rgba(0,0,0,0.1)',
      text: 'var(--color-text-primary, #333)',
      textMuted: 'var(--color-text-secondary, #666)',
      accent: 'var(--color-accent, #0066cc)',
      selected: 'rgba(0,0,0,0.05)',
    }
  }

  const colors = getThemeColors()

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: isTerminalTheme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0,0,0,0.5)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '80vh',
          backgroundColor: colors.bg,
          border: isTerminalTheme ? `1px solid ${colors.border}` : `1px solid ${colors.borderMuted}`,
          borderRadius: isTerminalTheme ? '4px' : isMacTheme ? '0' : '12px',
          boxShadow: isTerminalTheme
            ? `0 0 30px rgba(0, 255, 0, 0.3)`
            : '0 16px 48px rgba(0,0,0,0.2)',
          fontFamily: isTerminalTheme ? "'Courier New', monospace" : isMacTheme ? 'Chicago, Geneva, sans-serif' : 'inherit',
          zIndex: 9999,
          overflow: 'hidden',
        }}
        role="dialog"
        aria-label="Settings"
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isTerminalTheme ? '12px 16px' : '16px 20px',
            borderBottom: `1px solid ${colors.borderMuted}`,
            backgroundColor: colors.surface,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: isTerminalTheme ? '14px' : '16px',
              fontWeight: isTerminalTheme ? 'normal' : 600,
              color: colors.text,
            }}
          >
            {isTerminalTheme ? '> SETTINGS' : 'Settings'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                fontSize: '11px',
                color: colors.textMuted,
              }}
            >
              {isTerminalTheme ? 'ESC' : '⌘,'}
            </span>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: isTerminalTheme ? '14px' : '20px',
                cursor: 'pointer',
                color: colors.textMuted,
                padding: '4px',
                lineHeight: 1,
              }}
              aria-label="Close settings"
            >
              {isTerminalTheme ? '[x]' : '×'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: `1px solid ${colors.borderMuted}`,
            backgroundColor: colors.surface,
          }}
        >
          {(['layout', 'tokens', 'view', 'role'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: isTerminalTheme ? '8px' : '12px',
                backgroundColor: activeTab === tab ? colors.selected : 'transparent',
                color: activeTab === tab ? colors.text : colors.textMuted,
                border: 'none',
                borderBottom: activeTab === tab ? `2px solid ${colors.accent}` : '2px solid transparent',
                fontSize: isTerminalTheme ? '11px' : '13px',
                fontWeight: activeTab === tab ? 600 : 400,
                cursor: 'pointer',
                textTransform: isTerminalTheme ? 'uppercase' : 'capitalize',
                fontFamily: 'inherit',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          style={{
            padding: '16px',
            maxHeight: 'calc(80vh - 120px)',
            overflowY: 'auto',
          }}
        >
          {/* Layer 1: LayoutTheme */}
          {activeTab === 'layout' && (
            <div>
              <p style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
                {isTerminalTheme ? '# LAYER 1: HOW CONTENT IS DISPLAYED' : 'Layer 1: Controls HOW content is displayed'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {visibleThemes.map((theme) => (
                  <button
                    key={theme}
                    onClick={() => setLayoutTheme(theme)}
                    style={{
                      padding: '12px',
                      backgroundColor: currentTheme === theme ? colors.selected : 'transparent',
                      border: `1px solid ${currentTheme === theme ? colors.accent : colors.borderMuted}`,
                      borderRadius: isTerminalTheme ? '2px' : isMacTheme ? '0' : '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: colors.text,
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: currentTheme === theme ? 600 : 400 }}>
                      {isTerminalTheme && currentTheme === theme && '> '}
                      {!isTerminalTheme && currentTheme === theme && '✓ '}
                      {layoutLabels[theme]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Layer 2: DesignTokenSet */}
          {activeTab === 'tokens' && (
            <div>
              <p style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
                {isTerminalTheme ? '# LAYER 2: VISUAL STYLING' : 'Layer 2: Controls VISUAL styling'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {availableTokenSets.map((tokenSet) => (
                  <button
                    key={tokenSet}
                    onClick={() => setTokenSet(tokenSet)}
                    style={{
                      padding: '12px',
                      backgroundColor: currentTokenSet === tokenSet ? colors.selected : 'transparent',
                      border: `1px solid ${currentTokenSet === tokenSet ? colors.accent : colors.borderMuted}`,
                      borderRadius: isTerminalTheme ? '2px' : isMacTheme ? '0' : '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: colors.text,
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Color swatches (hide for terminal theme) */}
                      {!isTerminalTheme && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              backgroundColor: tokenColors[tokenSet].primary,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              backgroundColor: tokenColors[tokenSet].accent,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                        </div>
                      )}
                      <span style={{ fontWeight: currentTokenSet === tokenSet ? 600 : 400 }}>
                        {isTerminalTheme && currentTokenSet === tokenSet && '> '}
                        {!isTerminalTheme && currentTokenSet === tokenSet && '✓ '}
                        {tokenLabels[tokenSet]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Layer 3: ViewerProfile */}
          {activeTab === 'view' && (
            <div>
              <p style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
                {isTerminalTheme ? '# LAYER 3: CONTENT VISIBILITY' : 'Layer 3: Controls WHAT content is shown'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {availableProfiles.map((profile) => (
                  <button
                    key={profile}
                    onClick={() => setProfile(profile)}
                    style={{
                      padding: '12px',
                      backgroundColor: currentProfile === profile ? colors.selected : 'transparent',
                      border: `1px solid ${currentProfile === profile ? colors.accent : colors.borderMuted}`,
                      borderRadius: isTerminalTheme ? '2px' : isMacTheme ? '0' : '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: colors.text,
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: currentProfile === profile ? 600 : 400 }}>
                      {isTerminalTheme && currentProfile === profile && '> '}
                      {!isTerminalTheme && currentProfile === profile && '✓ '}
                      {viewLabels[profile]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Layer 4: RolePersona */}
          {activeTab === 'role' && (
            <div>
              <p style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
                {isTerminalTheme ? '# LAYER 4: POWER REVERSAL' : 'Layer 4: Controls WHO evaluates (Power Reversal)'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {availablePersonas.map((persona) => (
                  <button
                    key={persona}
                    onClick={() => setPersona(persona)}
                    style={{
                      padding: '12px',
                      backgroundColor: currentPersona === persona ? colors.selected : 'transparent',
                      border: `1px solid ${currentPersona === persona ? colors.accent : colors.borderMuted}`,
                      borderRadius: isTerminalTheme ? '2px' : isMacTheme ? '0' : '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: colors.text,
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: currentPersona === persona ? 600 : 400 }}>
                      {isTerminalTheme && currentPersona === persona && '> '}
                      {!isTerminalTheme && currentPersona === persona && '✓ '}
                      {roleLabels[persona]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SettingsModal
